import {
  dunningStageToDocumentType,
  formatDunningDeadlineDate,
  formatDunningGermanDate,
  formatDunningMoney,
  type DunningDocumentTextType,
  type DunningStage,
} from './dunningDocument';

export type { DunningStage };

export type DunningTemplateInput = {
  invoiceNumber: string | null;
  grossAmount: number | string | null;
  currency: string;
  dueDate: string | null;
  recipientName: string | null;
  reminderSentAt?: string | null;
  dunning1SentAt?: string | null;
  dunning2SentAt?: string | null;
  deadlineDays?: number;
  bodyTemplate?: string | null;
};

export type DunningEmail = {
  subject: string;
  body: string;
};

function greeting(recipientName: string | null): string {
  const name = (recipientName ?? '').trim();
  return name ? `Sehr geehrte/r ${name},` : 'Sehr geehrte Damen und Herren,';
}

function invoiceLabel(invoiceNumber: string | null): string {
  const ref = (invoiceNumber ?? '').trim();
  return ref || '—';
}

function collapseBlankLines(text: string): string {
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

export const DUNNING_DEFAULT_EMAIL_BODIES: Record<DunningDocumentTextType, string> = {
  Erinnerung: [
    '{{GREETING}}',
    '',
    'wir hoffen, es geht Ihnen gut!',
    '',
    'sicherlich ist es im täglichen Trubel untergegangen, aber wir haben gesehen, dass die Rechnung Nr. {{INVOICE_NUMBER}} über den Betrag von {{AMOUNT}} noch offen ist.',
    '',
    'Vielleicht finden Sie in den nächsten Tagen kurz Zeit, den Betrag zu überweisen? Falls sich unsere Nachricht mit Ihrer Zahlung überschnitten hat, betrachten Sie diese E-Mail bitte als gegenstandslos.',
  ].join('\n'),
  '1. Mahnung': [
    '{{GREETING}}',
    '',
    'leider müssen wir feststellen, dass unsere Rechnung Nr. {{INVOICE_NUMBER}} über {{AMOUNT}} (fällig am {{DUE_DATE}}) bisher nicht beglichen wurde.',
    '{{PREVIOUS_NOTICE}}',
    'Wir bitten Sie, den ausstehenden Betrag bis spätestens {{DEADLINE}} auf unser Konto zu überweisen.',
  ].join('\n'),
  '2. Mahnung': [
    '{{GREETING}}',
    '',
    'unsere 1. Mahnung vom {{DUNNING_1_DATE}} blieb leider ohne Reaktion. Die Rechnung Nr. {{INVOICE_NUMBER}} über {{AMOUNT}} ist weiterhin offen.',
    '',
    'Wir fordern Sie auf, den Betrag bis zum {{DEADLINE}} zu begleichen. Sollte keine Zahlung eingehen, sehen wir uns gezwungen, weitere Schritte einzuleiten.',
  ].join('\n'),
  '3. Mahnung': [
    '{{GREETING}}',
    '',
    'trotz mehrfacher Mahnungen ist die Zahlung der Rechnung Nr. {{INVOICE_NUMBER}} über {{AMOUNT}} (fällig am {{DUE_DATE}}) bis heute ausgeblieben.',
    '',
    'Dies ist unsere letzte Zahlungsaufforderung. Wenn wir bis zum {{DEADLINE}} keinen Zahlungseingang verbuchen, werden wir den Vorgang ohne weitere Ankündigung an ein Inkassobüro / unsere Rechtsabteilung übergeben.',
  ].join('\n'),
};

export function getDefaultDunningEmailBody(stage: DunningStage): string {
  return DUNNING_DEFAULT_EMAIL_BODIES[dunningStageToDocumentType(stage)];
}

function normalizeEmailTemplate(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\s+/g, ' ').trim();
}

/** Offer/invoice send-PDF copy accidentally stored on dunning document types. */
export function looksLikeGenericDocumentSendEmail(template: string): boolean {
  const n = normalizeEmailTemplate(template);
  if (!n) return false;
  if (n.includes('anbei schicke ich Ihnen unser') && n.includes('Schauen Sie doch mal drüber')) return true;
  if (n.includes('anbei übersenden wir Ihnen unsere Rechnung') && n.includes('unten angegebene Konto')) return true;
  return false;
}

export function resolveDunningEmailBodyTemplate(
  documentType: DunningDocumentTextType,
  stored: string | null | undefined,
): string {
  const fallback = DUNNING_DEFAULT_EMAIL_BODIES[documentType];
  const raw = (stored ?? '').trim();
  if (!raw || looksLikeGenericDocumentSendEmail(raw)) return fallback;
  return raw;
}

export function applyDunningEmailTemplate(
  template: string,
  values: {
    greeting: string;
    documentType: string;
    invoiceNumber: string;
    amount: string;
    dueDate: string;
    deadline: string;
    reminderDate: string;
    dunning1Date: string;
    dunning2Date: string;
    previousNotice: string;
  },
): string {
  return collapseBlankLines(
    template
      .replaceAll('{{GREETING}}', values.greeting)
      .replaceAll('{{DOCUMENT_TYPE}}', values.documentType)
      .replaceAll('{{INVOICE_NUMBER}}', values.invoiceNumber)
      .replaceAll('{{AMOUNT}}', values.amount)
      .replaceAll('{{DUE_DATE}}', values.dueDate)
      .replaceAll('{{DEADLINE}}', values.deadline)
      .replaceAll('{{REMINDER_DATE}}', values.reminderDate)
      .replaceAll('{{DUNNING_1_DATE}}', values.dunning1Date)
      .replaceAll('{{DUNNING_2_DATE}}', values.dunning2Date)
      .replaceAll('{{PREVIOUS_NOTICE}}', values.previousNotice),
  );
}

export function buildDunningEmail(stage: DunningStage, input: DunningTemplateInput): DunningEmail {
  const ref = invoiceLabel(input.invoiceNumber);
  const amount = formatDunningMoney(input.grossAmount, input.currency || 'EUR');
  const due = formatDunningGermanDate(input.dueDate);
  const deadline = formatDunningDeadlineDate(input.deadlineDays ?? 7);
  const open = greeting(input.recipientName);
  const documentType = dunningStageToDocumentType(stage);
  const reminderDate = formatDunningGermanDate(input.reminderSentAt ?? null);
  const dunning1Date = formatDunningGermanDate(input.dunning1SentAt ?? null);
  const dunning2Date = formatDunningGermanDate(input.dunning2SentAt ?? null);
  const previousNotice =
    stage === '1' && input.reminderSentAt
      ? `\nAuch unsere Zahlungserinnerung vom ${reminderDate} blieb leider ohne Reaktion.\n`
      : '';
  const template = (input.bodyTemplate ?? '').trim() || getDefaultDunningEmailBody(stage);
  const body = applyDunningEmailTemplate(template, {
    greeting: open,
    documentType,
    invoiceNumber: ref,
    amount,
    dueDate: due,
    deadline,
    reminderDate,
    dunning1Date,
    dunning2Date,
    previousNotice,
  });

  if (stage === 'reminder') {
    return { subject: `Kurze Erinnerung: Rechnung Nr. ${ref} vom ${due}`, body };
  }
  if (stage === '1') {
    return { subject: `1. Mahnung – Rechnung ${ref}`, body };
  }
  if (stage === '2') {
    return { subject: `2. Mahnung – Rechnung ${ref}`, body };
  }
  return { subject: `Letzte Mahnung – Rechnung ${ref} – drohende rechtliche Schritte`, body };
}

export const DUNNING_STAGE_LABEL: Record<DunningStage, string> = {
  reminder: 'Erinnerung',
  '1': '1. Mahnung',
  '2': '2. Mahnung',
  '3': '3. Mahnung',
};
